import { useMemo, useState } from 'react';
import { AutomationCampaignPicker } from '@/components/automation/AutomationCampaignPicker';
import {
  filterAutomationCampaigns,
  type AutomationCampaignOption,
} from '@/components/automation/automationCampaignScope';
import { cn } from '@/utils/cn';
import type { Portfolio } from '@/types/portfolios';

export type DaypartingScopeType = 'campaigns' | 'portfolio';

function portfolioMatches(portfolio: Portfolio, raw: string): boolean {
  const query = raw.trim().toLowerCase();
  if (!query) return true;
  return `${portfolio.name || ''} ${portfolio.portfolioId}`.toLowerCase().includes(query);
}

export function DaypartingScopePicker({
  scopeType,
  onScopeTypeChange,
  campaignOptions,
  selectedCampaigns,
  onCampaignsChange,
  campaignsLoading,
  portfolios,
  selectedPortfolioId,
  onPortfolioChange,
  portfoliosLoading,
  previewCampaigns,
  conflictCampaignIds,
  conflictLabel = 'Has dayparting',
  conflictNoun = 'dayparting',
  scopeNote = 'Base, hourly, and multipliers apply to each campaign.',
}: {
  scopeType: DaypartingScopeType;
  onScopeTypeChange: (next: DaypartingScopeType) => void;
  campaignOptions: AutomationCampaignOption[];
  selectedCampaigns: AutomationCampaignOption[];
  onCampaignsChange: (next: AutomationCampaignOption[]) => void;
  campaignsLoading?: boolean;
  portfolios: Portfolio[];
  selectedPortfolioId: string | null;
  onPortfolioChange: (portfolioId: string | null) => void;
  portfoliosLoading?: boolean;
  previewCampaigns: AutomationCampaignOption[];
  conflictCampaignIds?: Iterable<string>;
  conflictLabel?: string;
  conflictNoun?: string;
  scopeNote?: string;
}) {
  const [portfolioSearch, setPortfolioSearch] = useState('');
  const visiblePortfolios = useMemo(
    () => portfolios.filter((portfolio) => portfolioMatches(portfolio, portfolioSearch)).slice(0, 100),
    [portfolios, portfolioSearch]
  );
  const selectedCount =
    scopeType === 'campaigns' ? selectedCampaigns.length : previewCampaigns.length;
  const showPerCampaignNote = selectedCount > 1;
  const conflictSet = useMemo(
    () => new Set(conflictCampaignIds ?? []),
    [conflictCampaignIds]
  );
  const previewConflictCount = previewCampaigns.filter((campaign) =>
    conflictSet.has(campaign.campaignId)
  ).length;

  return (
    <div className="space-y-3">
      <div className="inline-flex rounded-lg border border-border p-0.5">
        <button
          type="button"
          className={cn(
            'rounded-md px-3 py-1.5 text-sm font-medium',
            scopeType === 'campaigns' ? 'bg-brand/10 text-foreground' : 'text-foreground-muted hover:text-foreground'
          )}
          onClick={() => onScopeTypeChange('campaigns')}
        >
          Campaigns
        </button>
        <button
          type="button"
          className={cn(
            'rounded-md px-3 py-1.5 text-sm font-medium',
            scopeType === 'portfolio' ? 'bg-brand/10 text-foreground' : 'text-foreground-muted hover:text-foreground'
          )}
          onClick={() => onScopeTypeChange('portfolio')}
        >
          Portfolio
        </button>
      </div>
      {scopeType === 'campaigns' ? (
        <AutomationCampaignPicker
          options={campaignOptions}
          selected={selectedCampaigns}
          onChange={onCampaignsChange}
          isLoading={campaignsLoading}
          emptyLabel="No campaigns available."
          loadingLabel="Loading campaigns…"
          conflictIds={conflictCampaignIds}
          conflictLabel={conflictLabel}
        />
      ) : (
        <div className="space-y-2 rounded border border-border p-3">
          <input
            type="search"
            value={portfolioSearch}
            onChange={(e) => setPortfolioSearch(e.target.value)}
            placeholder="Search portfolios…"
            className="w-full rounded border border-border px-3 py-2 text-sm"
          />
          {portfoliosLoading ? (
            <p className="text-xs text-foreground-muted">Loading portfolios…</p>
          ) : visiblePortfolios.length === 0 ? (
            <p className="text-xs text-foreground-muted">No portfolios available.</p>
          ) : (
            <ul className="m-0 max-h-40 list-none divide-y divide-border overflow-y-auto rounded border border-border bg-surface p-0">
              {visiblePortfolios.map((portfolio) => {
                const selected = selectedPortfolioId === portfolio.portfolioId;
                return (
                  <li key={portfolio.portfolioId}>
                    <button
                      type="button"
                      className={cn(
                        'flex w-full items-start gap-2 px-2.5 py-2 text-left text-sm hover:bg-surface-muted',
                        selected && 'bg-brand/10'
                      )}
                      onClick={() => onPortfolioChange(portfolio.portfolioId)}
                    >
                      <span
                        className={cn(
                          'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                          selected ? 'border-brand bg-brand' : 'border-border bg-surface'
                        )}
                        aria-hidden
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-foreground">
                          {portfolio.name || portfolio.portfolioId}
                        </span>
                        <span className="block truncate text-xs text-foreground-muted">
                          {portfolio.portfolioId}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {selectedPortfolioId ? (
            previewCampaigns.length > 0 ? (
              <p className="text-xs text-foreground-muted">
                {previewCampaigns.length} campaign{previewCampaigns.length === 1 ? '' : 's'} in this
                portfolio will get this schedule
                {previewConflictCount
                  ? `. ${previewConflictCount} already ${previewConflictCount === 1 ? 'has' : 'have'} ${conflictNoun}`
                  : ''}
                . Preview may omit campaigns beyond the loaded list; save still includes every
                member.
              </p>
            ) : (
              <p className="text-xs text-foreground-muted">
                No loaded campaigns in this portfolio. Save still resolves members on the server.
              </p>
            )
          ) : (
            <p className="text-xs text-foreground-muted">Select a portfolio.</p>
          )}
        </div>
      )}
      {showPerCampaignNote ? (
        <p className="text-xs text-foreground-muted">
          {scopeNote}
        </p>
      ) : null}
    </div>
  );
}

export function filterCampaignsByPortfolio(
  options: AutomationCampaignOption[],
  portfolioId: string | null
): AutomationCampaignOption[] {
  if (!portfolioId) return [];
  return filterAutomationCampaigns(
    options.filter((option) => option.portfolioId === portfolioId),
    ''
  );
}
